<template>
	<div>
		<v-main>
			<v-container fluid fill-height>
				<v-layout justify-center align-center>
					<v-card width="100%" outlined elevation-0>
						<v-toolbar flat class="border_bottom" shaped>
							<v-toolbar-title>Associa prodotti {{ platformLabel }}</v-toolbar-title>
							<v-btn class="ml-6" outlined color="primary" data-tour="tutorial" @click="tour.start()">
								<v-icon left>mdi-school-outline</v-icon>
								Tutorial guidato
							</v-btn>
							<v-spacer></v-spacer>
							<v-btn text data-tour="refresh" :disabled="!marketplace" :loading="loading" @click="refresh">
								<v-icon left>mdi-refresh</v-icon>
								Ricarica
							</v-btn>
							<v-btn color="primary" data-tour="scan" :disabled="!marketplace || scanRunning" :loading="sending" @click="startScan">
								<v-icon left>mdi-cloud-download-outline</v-icon>
								Aggiorna elenco
							</v-btn>
							<template v-slot:extension>
								<MarketplaceTabs :platform="platform" />
							</template>
						</v-toolbar>

						<v-col cols="12">
							<v-row>
								<v-col cols="12" md="4" data-tour="country">
									<v-select
										v-model="marketplace"
										:items="marketplaceOptions"
										:loading="loadingMarketplaces"
										label="Paese"
										item-text="text"
										item-value="value"
										outlined
										dense
										hide-details
										@change="changeMarketplace"
									></v-select>
								</v-col>
								<v-col cols="12" md="4" data-tour="search">
									<v-text-field
										v-model="searchfield"
										label="Cerca SellerSku, codice o titolo"
										outlined
										append-icon="mdi-magnify"
										dense
										hide-details
										clearable
										@click:append="applySearch"
										@keyup.enter="applySearch"
									></v-text-field>
								</v-col>
								<!-- solo Amazon: il report ha Active/Inactive, per eBay i valori non sono verificati -->
								<v-col v-if="platform === 'amazon'" cols="12" md="4">
									<v-select v-model="statusFilter" :items="statusOptions" label="Status" outlined dense hide-details @change="applyStatus"></v-select>
								</v-col>
							</v-row>

							<v-alert v-if="lastError" data-tour="status" type="error" text dense class="mt-4 mb-0">{{ lastError }}</v-alert>
							<v-alert v-else-if="scanRunning" data-tour="status" type="info" text dense class="mt-4 mb-0">
								Elenco in preparazione: il report di {{ platformLabel }} può richiedere qualche minuto. Riceverai una notifica quando è pronto.
								<br />
								<small> Richiesta delle {{ scan.created_at | moment('HH:mm') }} — {{ scan.status === 'queued' ? 'in attesa di essere presa in carico' : 'lettura del report in corso' }}. </small>
							</v-alert>
							<v-alert v-else-if="scanFailed" data-tour="status" type="error" text dense class="mt-4 mb-0">
								L'ultimo aggiornamento non è riuscito: {{ scan.error }}
								<br />
								<small> Richiesta delle {{ scan.created_at | moment('HH:mm') }} — {{ scan.job_id ? `job #${scan.job_id} accodato regolarmente` : 'nessun job accodato' }}. </small>
								<br v-if="scan.error_at" />
								<small v-if="scan.error_at">{{ scan.error_at }}</small>
							</v-alert>
							<v-alert v-else-if="scan" data-tour="status" type="success" text dense class="mt-4 mb-0">
								Ultimo aggiornamento del {{ scan.updated_at | moment('DD/MM/YYYY HH:mm') }} — {{ total }} pubblicazioni senza prodotto associato.
								<span v-if="noSku > 0"> — {{ noSku }} pubblicazioni sono senza SellerSku e non sono agganciabili da qui.</span>
								<!-- ripartizione per paese di tutte le inserzioni attive: la somma e' il totale eBay -->
								<div v-if="ripartizionePaesi" class="caption">
									Su eBay hai <strong>{{ ripartizionePaesi.totale }}</strong> inserzioni attive:
									<span v-for="(paese, i) in ripartizionePaesi.paesi" :key="paese.nome">{{ i > 0 ? ', ' : '' }}{{ paese.nome }} {{ paese.n }}</span
									>.
								</div>
								<!-- il sito di ogni inserzione arriva da GetSellerList: qui c'e' solo cio' che e'
									 stato creato su questo eBay, con il prezzo e la valuta di quell'inserzione -->
								<div v-if="platform === 'ebay' && paeseSelezionato && paeseSelezionato.sitoLetto !== false" class="caption">
									Qui trovi solo le inserzioni create su questo eBay, con il loro prezzo e la loro valuta. In quali altri paesi sono visibili lo vedi dal dettaglio del prodotto.
								</div>
							</v-alert>
							<!-- scansione fatta senza riconoscere il paese delle inserzioni (eBay non ha
								 risposto a GetSellerList, o l'ha eseguita un processo non aggiornato) -->
							<v-alert v-if="platform === 'ebay' && !scanRunning && paeseSelezionato && paeseSelezionato.sitoLetto === false" type="warning" text dense class="mt-2 mb-0">
								L'ultimo aggiornamento non ha riconosciuto in quale paese è stata creata ciascuna inserzione: questo elenco contiene le inserzioni di <strong>tutti</strong> i paesi eBay, per questo è
								uguale ovunque. Premi <strong>Aggiorna elenco</strong> per rifarlo.
							</v-alert>
							<!-- condizione esplicita: come v-else dell'avviso eBay qui sopra compariva sempre su Amazon -->
							<v-alert v-if="!lastError && !scanRunning && !scanFailed && !scan" data-tour="status" type="info" text dense class="mt-4 mb-0">
								Nessun elenco ancora scaricato per questo paese: premi <strong>Aggiorna elenco</strong> per leggere le pubblicazioni presenti su {{ platformLabel }}.
							</v-alert>
						</v-col>

						<v-data-table
							data-tour="table"
							:headers="headers"
							:items="items"
							:options.sync="options"
							:server-items-length="total"
							:loading="loading"
							class="elevation-0"
							:footer-props="{ 'items-per-page-options': listSize }"
							item-key="_id"
							:hide-default-header="mobileCards"
							:class="{ 'table-cards': mobileCards }"
						>
							<!-- telefono: ogni riga diventa una card chiave/valore (lo slot body esiste solo in questa vista) -->
							<template v-if="mobileCards" v-slot:body="{ items: righe }">
								<tbody class="unmatched-cards">
									<tr v-if="righe.length === 0">
										<td :colspan="headers.length" class="px-3 py-1">
											<div class="grey--text text-center py-4">{{ loading ? 'Caricamento...' : 'Nessun elemento' }}</div>
										</td>
									</tr>
									<tr v-for="item in righe" :key="item._id">
										<td :colspan="headers.length" class="px-3 py-1">
											<key-value-card :title="item.title || item.sellerSku || '-'" :fields="campiCard(item)" :image="item.image || ''">
												<template v-slot:actions>
													<v-btn small color="primary" text data-tour="associa" @click="associa(item)">
														<v-icon left small>mdi-link-variant</v-icon>
														Associa
													</v-btn>
												</template>
												<template v-slot:top>
													<div v-if="item.site" class="caption grey--text">{{ item.site }}</div>
												</template>
												<div class="d-flex justify-space-between">
													<span class="card-label">Codice</span>
													<a v-if="item.url" :href="item.url" target="_blank" rel="noopener">{{ item.code }}</a>
													<span v-else>{{ item.code || '-' }}</span>
												</div>
												<div class="d-flex justify-space-between">
													<span class="card-label">Prezzo</span>
													<span v-if="item.price === null || item.price === undefined">-</span>
													<span v-else class="font-weight-bold">
														{{ item.price }}
														<span v-if="item.currency" :class="{ 'red--text': isValutaDiversa(item) }">{{ item.currency }}</span>
													</span>
												</div>
											</key-value-card>
										</td>
									</tr>
								</tbody>
							</template>
							<template v-slot:item.image="{ item }">
								<v-img v-if="item.image" :src="item.image" max-height="48" max-width="48" class="my-1"></v-img>
								<v-icon v-else color="grey lighten-1">mdi-image-off-outline</v-icon>
							</template>
							<template v-slot:item.code="{ item }">
								<a v-if="item.url" :href="item.url" target="_blank" rel="noopener">{{ item.code }}</a>
								<span v-else>{{ item.code }}</span>
							</template>
							<template v-slot:item.title="{ item }">
								<span v-if="item.title">{{ item.title }}</span>
								<span v-else class="grey--text">—</span>
								<!-- sito di origine dell'inserzione (da GetSellerList, o da GetItem con titolo e immagine) -->
								<div v-if="item.site" class="caption grey--text">{{ item.site }}</div>
							</template>
							<template v-slot:item.price="{ item }">
								<span v-if="item.price === null || item.price === undefined">-</span>
								<span v-else>
									{{ item.price }}
									<!-- la valuta non e' quella del paese: su eBay Italia ci sono inserzioni in sterline -->
									<span v-if="item.currency" :class="{ 'red--text': isValutaDiversa(item) }">{{ item.currency }}</span>
								</span>
							</template>
							<template v-slot:item.quantity="{ item }">
								{{ item.quantity === null || item.quantity === undefined ? '-' : item.quantity }}
							</template>
							<template v-slot:item.actions="{ item }">
								<v-btn small color="primary" text data-tour="associa" @click="associa(item)">
									<v-icon left small>mdi-link-variant</v-icon>
									Associa
								</v-btn>
							</template>
						</v-data-table>
					</v-card>

					<v-snackbar v-model="snackbar.snackbar" :color="snackbar.color" :timeout="snackbar.timeout">
						{{ snackbar.text }}
						<v-btn dark text @click="snackbar.snackbar = false">Close</v-btn>
					</v-snackbar>
				</v-layout>
			</v-container>
		</v-main>

		<!-- Ultimo elemento: il nodo viene spostato in `[data-app]` al montaggio. -->
		<TourOverlay :tour="tour"></TourOverlay>
	</div>
</template>

<script>
import { getCurrentInstance } from 'vue';
import { mapGetters, mapMutations } from 'vuex';
import { regions as amazonRegions } from '@/utils/amazonPartner';
import { regions as ebayRegions } from '@/utils/ebay/marketplace';
import TourOverlay from '@/components/core/tour/TourOverlay.vue';
import { useTour } from '@/components/core/tour/useTour';
import { createUnmatchedTourSteps } from './unmatched-tour';
import KeyValueCard from '@/components/core/KeyValueCard.vue';
import MarketplaceTabs from '@/components/marketplace/MarketplaceTabs';

import { openWithReturn } from '@/components/core/pageReturn';
import { salvaPubblicazione } from '@/components/marketplace/syncHandoff';
import { priceAnalysisRouteState } from '@/components/marketplace/priceAnalysisRouteState';

// Pubblicazioni presenti sul marketplace ma senza prodotto associato da noi.
// La stessa pagina serve Amazon ed eBay: cambia solo `platform`, preso dalla route.
export default {
	name: 'MarketplaceUnmatched',
	components: { TourOverlay, KeyValueCard, MarketplaceTabs },
	// marketplace, ricerca, stato e pagina nell'URL: refresh e ritorno dall'associazione ripartono da li'
	mixins: [priceAnalysisRouteState('statusFilter')],
	/*
	 * Il controller del tutorial nasce qui e non in `data()`: quello che sta in
	 * `data()` viene reso reattivo in profondita' da Vue 2, e in quel passaggio i
	 * `ref` del motore verrebbero srotolati nel loro valore grezzo.
	 *
	 * I passi si passano come funzione perche' i testi nominano il marketplace, che
	 * si sa solo leggendo la route: in `setup()` non c'e' `this`, e la funzione
	 * viene valutata all'avvio del giro, quando l'istanza e' ormai pronta.
	 */
	setup() {
		const instance = getCurrentInstance();

		return {
			tour: useTour(() => createUnmatchedTourSteps({ platformLabel: instance.proxy.platformLabel })),
		};
	},
	data() {
		return {
			marketplace: null,
			activeMarketplaces: [],
			loadingMarketplaces: false,
			loading: false,
			sending: false,
			searchfield: '',
			search: '',
			statusFilter: '',
			statusOptions: [
				{ text: 'Tutti', value: '' },
				{ text: 'Attivi', value: 'active' },
				// comprende anche Incomplete e status vuoto
				{ text: 'Inattivi', value: 'inactive' },
			],
			items: [],
			total: 0,
			scan: null,
			lastError: '',
			pollTimer: null,
			componentKey: 0,
			operation: '',
			// riga in corso di associazione: serve a toglierla dalla lista a conferma avvenuta
			current: null,
			options: {
				page: 1,
				itemsPerPage: 25,
			},
			listSize: [25, 50, 100],
			snackbar: {
				snackbar: false,
				color: '',
				timeout: 6000,
				text: '',
			},
			headers: [
				{ text: '', value: 'image', sortable: false, width: 70 },
				{ text: 'Seller Sku', value: 'sellerSku', sortable: false },
				{ text: 'Codice', value: 'code', sortable: false },
				{ text: 'Titolo', value: 'title', sortable: false },
				{ text: 'Prezzo', value: 'price', sortable: false },
				{ text: 'Quantità', value: 'quantity', sortable: false },
				{ text: 'Status', value: 'status', sortable: false },
				{ text: '', value: 'actions', sortable: false, align: 'end' },
			],
		};
	},
	computed: {
		...mapGetters({
			getDialog: 'settings/getDialog',
		}),
		// sotto i 769px le righe diventano card
		mobileCards() {
			return this.$vuetify.breakpoint.width < 769;
		},
		platform() {
			return this.$route.meta.platform === 'ebay' ? 'ebay' : 'amazon';
		},
		platformLabel() {
			return this.platform === 'ebay' ? 'eBay' : 'Amazon';
		},
		scanRunning() {
			if (!this.scan || (this.scan.status !== 'queued' && this.scan.status !== 'running')) {
				return false;
			}
			// una scansione ferma da mezz'ora non e' in corso, e' appesa: il bottone deve
			// tornare premibile comunque, altrimenti non c'e' modo di riprovare
			return Date.now() - new Date(this.scan.created_at).getTime() < 30 * 60 * 1000;
		},
		scanFailed() {
			return !!this.scan && this.scan.status === 'failed';
		},
		// pubblicazioni che il marketplace espone senza SellerSku: non si possono agganciare per SKU
		noSku() {
			return (this.scan && this.scan.counters && this.scan.counters.noSku) || 0;
		},
		// elenco di riserva quando il profilo non ha ancora pubblicazioni note
		fallbackMarketplaces() {
			const regions = this.platform === 'ebay' ? ebayRegions : amazonRegions;
			return regions.filter(region => region.MarketplaceId && region.marketplace).map(region => ({ text: region.marketplace, value: region.MarketplaceId }));
		},
		// si mostrano i marketplace dove abbiamo davvero pubblicazioni, come in "Scarica catalogo"
		marketplaceOptions() {
			if (this.activeMarketplaces.length === 0) {
				return this.fallbackMarketplaces;
			}
			return this.activeMarketplaces.map(item => {
				const known = this.fallbackMarketplaces.find(option => option.value === item.marketplaceId);
				const nome = known ? known.text : item.marketplaceId;
				return {
					text: this.etichettaPaese(nome, item),
					value: item.marketplaceId,
				};
			});
		},
		// tutte le inserzioni eBay divise per paese di creazione (dall'ultimo aggiornamento)
		ripartizionePaesi() {
			const siti = this.platform === 'ebay' && this.scan && this.scan.counters && this.scan.counters.siti;
			if (!siti || siti['(assente)'] === this.scan.counters.report) {
				return null;
			}
			const paesi = Object.keys(siti)
				.map(siteId => {
					const region = ebayRegions.find(r => String(r.SiteId) === String(siteId));
					return { nome: siteId === '(assente)' ? 'paese non riconosciuto' : region ? region.marketplace : `sito ${siteId}`, n: siti[siteId] };
				})
				.sort((a, b) => b.n - a.n);
			return { totale: paesi.reduce((tot, paese) => tot + paese.n, 0), paesi };
		},
		// conteggi del paese aperto: da quante inserzioni viene l'elenco
		paeseSelezionato() {
			return this.activeMarketplaces.find(item => item.marketplaceId === this.marketplace) || null;
		},
	},
	watch: {
		// Amazon ed eBay usano la stessa view: passando dall'uno all'altro dal menu Vue riusa la pagina
		// senza rifare created(), e restavano paesi ed elenco della piattaforma di prima
		async platform() {
			this.stopPolling();
			Object.assign(this.$data, this.$options.data.call(this));
			this.leggiStatoUrl();
			await this.loadMarketplaces();
		},
		options: {
			handler: function () {
				this.getDataFromApi();
			},
			deep: true,
		},
	},
	async created() {
		this.setDialog(false);
		this.leggiStatoUrl();
		await this.loadMarketplaces();
	},
	beforeDestroy() {
		// Se si lascia la pagina a giro aperto, vanno staccati i listener del tutorial.
		this.tour.stop();
		this.stopPolling();
	},
	methods: {
		...mapMutations({
			setDialog: 'settings/setDialog',
			setNotify: 'notify/setItem',
			setClearError: 'settings/setClearError',
			setMessages: 'settings/setMessages',
		}),
		// il numero e' quello delle righe da associare, non dei prodotti gia' associati
		// (che qui sono proprio quelli che mancano: "Italia (0)" con 33.000 pubblicazioni confondeva)
		etichettaPaese(nome, item) {
			if (item.listings === undefined) {
				// backend non aggiornato: niente conteggi dell'elenco
				return `${nome} (${item.total})`;
			}
			if (item.listings === null) {
				return `${nome} — da aggiornare`;
			}
			return `${nome} — ${item.unmatched} da associare`;
		},
		async loadMarketplaces() {
			this.loadingMarketplaces = true;
			const res = await this.$store.dispatch('marketplaceCatalog/marketplaces', { platform: this.platform });
			this.loadingMarketplaces = false;
			this.activeMarketplaces = res && !res.error && Array.isArray(res.marketplaces) ? res.marketplaces : [];

			// si preseleziona solo il marketplace piu' usato dal profilo: nell'elenco di riserva
			// il primo sarebbe un paese a caso, e si finirebbe per scansionare quello
			if (this.marketplace && !this.marketplaceOptions.some(o => o.value === this.marketplace)) {
				this.marketplace = null;
			}
			if (this.marketplace) {
				await this.loadScan();
				await this.getDataFromApi();
			} else if (this.activeMarketplaces.length > 0) {
				this.marketplace = this.marketplaceOptions[0].value;
				await this.changeMarketplace();
			}
		},
		// solo i numeri della tendina, senza toccare il paese scelto
		async refreshCounts() {
			const res = await this.$store.dispatch('marketplaceCatalog/marketplaces', { platform: this.platform });
			if (res && !res.error && Array.isArray(res.marketplaces)) {
				this.activeMarketplaces = res.marketplaces;
			}
		},
		async changeMarketplace() {
			this.options.page = 1;
			this.items = [];
			this.total = 0;
			await this.loadScan();
			await this.getDataFromApi();
		},
		// rilegge stato e lista senza far ripartire il job
		async refresh() {
			await this.loadScan();
			await Promise.all([this.getDataFromApi(), this.refreshCounts()]);
		},
		// valuta diversa da quella del paese selezionato: si evidenzia, altrimenti un
		// prezzo in sterline in mezzo agli euro passa inosservato
		isValutaDiversa(item) {
			const regions = this.platform === 'ebay' ? ebayRegions : amazonRegions;
			const region = regions.find(r => r.MarketplaceId === this.marketplace);
			return !!(region && region.currency && item.currency && region.currency !== item.currency);
		},
		// coppie chiave/valore della card su telefono (codice e prezzo sono resi a parte: link e valuta evidenziata)
		campiCard(item) {
			return [
				{ label: 'Seller Sku', value: item.sellerSku, strong: true },
				{ label: 'Quantità', value: item.quantity },
				{ label: 'Status', value: item.status },
			];
		},
		applySearch() {
			this.search = this.searchfield || '';
			this.options.page = 1;
			this.getDataFromApi();
		},
		applyStatus() {
			this.options.page = 1;
			this.getDataFromApi();
		},
		async loadScan() {
			if (!this.marketplace) {
				return;
			}
			const res = await this.$store.dispatch('marketplaceUnmatched/scanStatus', { platform: this.platform, marketplace: this.marketplace });
			this.scan = res && !res.error ? res.scan : null;

			if (this.scanRunning) {
				this.startPolling();
			} else {
				this.stopPolling();
			}
		},
		// il job dura minuti: si ricontrolla ogni tanto invece di lasciare la pagina ferma
		startPolling() {
			if (this.pollTimer) {
				return;
			}
			this.pollTimer = setInterval(async () => {
				const before = this.scan && this.scan.status;
				await this.loadScan();
				// appena il job esce dallo stato "in corso" si ricarica: sia che sia andato
				// bene, sia per mostrare subito l'errore invece di lasciare la pagina vuota
				if ((before === 'running' || before === 'queued') && !this.scanRunning) {
					await Promise.all([this.getDataFromApi(), this.refreshCounts()]);
				}
			}, 15000);
		},
		stopPolling() {
			if (this.pollTimer) {
				clearInterval(this.pollTimer);
				this.pollTimer = null;
			}
		},
		async getDataFromApi() {
			if (!this.marketplace) {
				return;
			}
			this.loading = true;
			const res = await this.$store.dispatch('marketplaceUnmatched/rows', {
				platform: this.platform,
				marketplace: this.marketplace,
				page: this.options.page,
				limit: this.options.itemsPerPage,
				search: this.search,
				status: this.platform === 'amazon' ? this.statusFilter : '',
			});
			this.loading = false;
			this.scriviStatoUrl();

			if (res && res.error) {
				this.items = [];
				this.total = 0;
				return;
			}
			this.items = res.items || [];
			this.total = res.total || 0;
			this.ripristinaPaginaUrl();
		},
		async startScan() {
			this.sending = true;
			const res = await this.$store.dispatch('marketplaceUnmatched/scan', { platform: this.platform, marketplace: this.marketplace });
			this.sending = false;

			if (res && res.error) {
				this.notify(this.scanErrorText(res), 'error');
				this.lastError = this.scanErrorText(res);
				await this.loadScan();
				return;
			}
			this.lastError = '';

			this.scan = res.scan;
			this.notify('Elenco in preparazione: può richiedere qualche minuto, riceverai una notifica quando è pronto.', 'info');
			this.startPolling();
		},
		/**
		 * Apre la stessa maschera di associazione usata dalle notifiche: legge SellerSku,
		 * marketplace e codice dallo store `notify`, quindi le si passa la riga in quel formato.
		 */
		associa(item) {
			// la maschera di associazione e' una pagina: Indietro (o il salvataggio) torna qui
			salvaPubblicazione({
				title: `Associa la pubblicazione <strong>${item.sellerSku}</strong> (${item.code || '-'}) a un prodotto`,
				sku: item.sellerSku,
				marketplace: item.marketplaceId,
				code: item.code,
				// dice alla maschera che l'associazione arriva da qui: salvera' la sola
				// pubblicazione invece di riscrivere tutto il prodotto (che va in timeout)
				unmatchedRowId: item._id,
				unmatchedPlatform: this.platform,
			});
			openWithReturn(this, { path: `/marketplace/associa/${this.platform}` }, { label: 'Torna ad Associa prodotti' });
		},
		// La riga l'ha gia' tolta il server insieme all'associazione: qui si aggiorna
		// soltanto quello che si vede, senza richiedere una cancellazione gia' avvenuta.
		onAssociated() {
			const row = this.current;
			this.current = null;
			if (!row) {
				return;
			}

			this.items = this.items.filter(item => item._id !== row._id);
			this.total = Math.max(0, this.total - 1);
		},
		/**
		 * Un errore muto qui e' il caso peggiore: la pagina resta vuota e sembra che il
		 * job sia solo lento. Si dice sempre cosa e' andato storto.
		 */
		scanErrorText(res) {
			if (res.status === 409) {
				return 'Un aggiornamento per questo paese è già in corso.';
			}
			if (res.status === 404) {
				return 'Questa sezione non è disponibile su questo server: il backend non è aggiornato.';
			}
			if (res.status === 422 && String(res.error).indexOf('not_configured') !== -1) {
				return `Le credenziali ${this.platformLabel} non sono configurate per questo profilo.`;
			}
			return `Errore durante la richiesta${res.error ? `: ${res.error}` : ''}.`;
		},
		propSetMessages(text) {
			this.notify(text, 'success');
			this.operation = '';
		},
		notify(text, color) {
			this.snackbar.color = color;
			this.snackbar.text = text;
			this.snackbar.snackbar = true;
		},
	},
};
</script>

<style scoped>
/* vista card: niente layout a colonne, ogni riga e' un blocco e la card prende il 100% */
.table-cards ::v-deep table {
	display: block;
	width: 100%;
}
.unmatched-cards,
.unmatched-cards tr {
	display: block;
	width: 100%;
}
.unmatched-cards tr,
.unmatched-cards tr:hover {
	background: transparent !important;
}
.unmatched-cards td {
	display: block;
	width: 100%;
	border-bottom: none !important;
	height: auto !important;
}
/* respiro tra le card e il bordo della card che contiene la tabella */
.unmatched-cards tr:first-child td {
	padding-top: 12px !important;
}
.unmatched-cards tr:last-child td {
	padding-bottom: 12px !important;
}
.unmatched-cards tr + tr td {
	padding-top: 8px !important;
}
.card-label {
	font-size: 14px;
	color: rgba(0, 0, 0, 0.6);
}
/* card in lista fuori da v-data-table */
.kv-list > .kv-card + .kv-card {
	margin-top: 8px;
}
</style>
